import { Suspense, lazy, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useBehavior, useBehaviorMulti, useLineage } from "../api/artifacts";
import { useGradientStatus, useRunDetail, useRuns, useStopRun, useTopology } from "../api/queries";
import type { ArchDesc, BehaviorLogEntry } from "../api/types";
import { useRunSocket } from "../ws/useRunSocket";
import { Card } from "../components/ui";
import { FleetStrip } from "../components/FleetStrip";
import { RunSelector } from "../components/RunSelector";
import { fmtBytes } from "../lib/format";
import { FitnessCanvas } from "../viz/FitnessCanvas";
import { BehaviorCanvas } from "../viz/BehaviorCanvas";
import { PhyloSvg } from "../viz/PhyloSvg";
import { NetworkSvg } from "../viz/NetworkSvg";

const FitnessLandscape3D = lazy(() =>
  import("../viz/FitnessLandscape3D").then((m) => ({ default: m.FitnessLandscape3D })));
const FlightPath3D = lazy(() =>
  import("../viz/FlightPath3D").then((m) => ({ default: m.FlightPath3D })));

function Window({ title, area, wide, tall, children }: {
  title: string; area?: string; wide?: boolean; tall?: boolean; children: ReactNode;
}) {
  // `area` places the window in the desktop grid (win-fitness, win-network, …);
  // wide/tall drive the mobile single-column sizing.
  return (
    <section className={`win${area ? ` win-${area}` : ""}${wide ? " win-wide" : ""}`}>
      <header className="win-head">{title}</header>
      <div className={`viz-host${tall ? " viz-host-tall" : ""}`}>{children}</div>
    </section>
  );
}

function fmtEta(s: number): string {
  const m = Math.floor(s / 60), sec = Math.round(s % 60);
  return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}m` : `${m}m${String(sec).padStart(2, "0")}s`;
}

// The Evolution Console: login lands here. A fleet strip + run selector on top,
// the selected run's full window set below. Runs are launched out-of-band (the
// orchestrator API, driven from Claude Code), not from a form.
export function Dashboard() {
  const { name: urlName } = useParams();
  const runs = useRuns().data ?? [];
  // Default the dropdown to the running run on the lowest online GPU (GPU0 first);
  // only an explicit URL selection (a manual pick) overrides it.
  const fallback =
    [...runs].filter((r) => r.status === "running")
      .sort((a, b) => (a.gpu_id ?? Infinity) - (b.gpu_id ?? Infinity))[0]?.name
    ?? runs[0]?.name ?? "";
  const name = urlName ?? fallback;

  const stream = useRunSocket(name);
  const running = stream.status === "running";
  // Live updates for ANY selected run that isn't terminal. Claude-launched runs have no
  // WS job, so the windows are driven by POLLING the file artifacts (lineage/behavior/detail).
  const live = !["finished", "killed", "failed"].includes(stream.status);
  const stop = useStopRun();

  const summary = runs.find((r) => r.name === name);
  const detail = useRunDetail(name, live).data;
  const archDescriptor = detail?.metadata?.arch_descriptor as ArchDesc | undefined;
  const lineage = useLineage(name, live).data ?? [];
  const behavior = useBehavior(name, live).data ?? null;
  const behaviorMulti = useBehaviorMulti(name, live).data ?? null;   // best across all placements
  const topology = useTopology().data;

  const lastRow = lineage.at(-1);
  const estMax = lastRow && Number.isFinite(lastRow.est_max_fitness) ? lastRow.est_max_fitness : null;
  const bestGenome = (() => {
    for (let i = lineage.length - 1; i >= 0; i--) if (lineage[i].best) return lineage[i].best;
    return null;
  })();
  // TOP GENOMES, no dropdown (Derek 2026-08-03): the current gen's top-k as always-visible
  // clickable chips, fitness on the label. Clicking one drives the Network window; "best"
  // (best-so-far) is the default and stays first. Selection resets when the run changes.
  const topK = lastRow?.top_k ?? [];
  const [selRank, setSelRank] = useState<number | null>(null);
  useEffect(() => { setSelRank(null); }, [name]);
  const selEntry = selRank != null ? topK.find((e) => e.rank === selRank) ?? null : null;
  const shownGenome = selEntry?.genome ?? bestGenome;
  // Fitness curve + header: prefer the live WS gen-stream (orchestrator jobs); otherwise
  // rebuild them from the polled lineage so Claude-launched runs animate too.
  const genRows = stream.genRows.length
    ? stream.genRows
    : lineage.map((r) => ({ gen: r.gen, gen_best: r.fitness_gen_best,
        best_so_far: r.fitness_best_so_far, mean: r.fitness_gen_mean, wall_s: 0, eta: "" }));
  const bestSoFar = stream.bestSoFar ?? lastRow?.fitness_best_so_far ?? null;
  const bestGen = stream.bestGen ?? lastRow?.gen ?? null;
  // Run facts from the metadata + lineage, for the top status bar + progress.
  const meta = detail?.metadata ?? {};
  const algo = (meta.searcher as string | undefined) ?? null;
  const popsize = (meta.popsize as number | undefined) ?? null;
  const totalGens = (meta.generations as number | undefined) ?? null;
  const curGen = lastRow?.gen ?? bestGen ?? null;
  const meanFit = lastRow?.fitness_gen_mean ?? null;
  const recentWall = lineage.slice(-8).map((r) => r.wall_s).filter((w): w is number => !!w && w > 0);
  const avgWall = recentWall.length ? recentWall.reduce((a, b) => a + b, 0) / recentWall.length : null;
  const etaSec = totalGens != null && curGen != null && avgWall ? (totalGens - curGen) * avgWall : null;
  const progress = totalGens && curGen != null ? Math.min(1, curGen / totalGens) : null;
  // Best organism's steady-state motor output (mean over the last half of its trajectory),
  // shown at the motor nodes in the Network window. Prefer the per-NEURON signed output
  // (ctrnn4: 8 values, fwd +σ / rev −σ → one per output node); fall back to the 4 actuator cmds.
  const motorOut = (() => {
    const useNeurons = !!behavior?.log?.some((e) => e.motor_neurons);
    const vals = (e: BehaviorLogEntry) => (useNeurons ? e.motor_neurons : e.cmd);
    const log = behavior?.log?.filter((e) => vals(e)) ?? [];
    if (!log.length) return null;
    const tail = log.slice(Math.floor(log.length / 2));
    const n = vals(tail[0])!.length;
    const sum = new Array<number>(n).fill(0);
    for (const e of tail) vals(e)!.forEach((v, i) => { sum[i] += v; });
    return sum.map((s) => s / tail.length);
  })();

  return (
    <div className="dash">
      <GradientBanner />
      <header className="dash-bar">
        <FleetStrip />
        <RunSelector selected={name} />
      </header>

      {!name ? (
        <Card><p className="dim">No runs yet. Launch one from Claude Code; it'll appear here.</p></Card>
      ) : (
        <>
          <div className="dash-status mono">
            <span className={`chip chip-${stream.status}`}>{stream.status}</span>
            <span><i className="dim">best</i> {bestSoFar?.toFixed(4) ?? "—"}</span>
            <span><i className="dim">mean</i> {meanFit != null ? meanFit.toFixed(4) : "—"}</span>
            <span><i className="dim">gen</i> {curGen ?? "—"}</span>
            <span><i className="dim">algo</i> {algo ?? "—"}</span>
            <span><i className="dim">pop</i> {popsize ?? "—"}</span>
            {summary && <span><i className="dim">size</i> {fmtBytes(summary.size_bytes)}</span>}
            <span className="dim ws-conn"><ConnDot conn={stream.conn} /></span>
            <span className="dash-actions">
              {running && (
                <button className="danger" onClick={() => stop.mutate(name)} disabled={stop.isPending}>
                  {stop.isPending ? "Stopping…" : "Stop"}
                </button>
              )}
            </span>
          </div>

          {topK.length > 0 && (
            <div className="topk-strip mono">
              <span className="dim topk-label">top genomes · gen {lastRow?.gen}</span>
              <button className={`topk-btn${selRank == null ? " topk-sel" : ""}`}
                      onClick={() => setSelRank(null)}
                      title="best-so-far champion (drives the Network window)">
                best {bestSoFar != null ? bestSoFar.toFixed(3) : "—"}
              </button>
              {topK.slice(0, 10).map((e) => (
                <button key={e.rank}
                        className={`topk-btn${selRank === e.rank ? " topk-sel" : ""}`}
                        onClick={() => setSelRank(e.rank)}
                        title={`gen-${lastRow?.gen} rank ${e.rank + 1} → Network window`}>
                  #{e.rank + 1} {e.fitness.toFixed(3)}
                </button>
              ))}
            </div>
          )}

          {progress != null && (
            <div className="dash-progress">
              <div className="meter">
                <div className="meter-fill" style={{ width: `${(progress * 100).toFixed(1)}%`, background: "var(--accent)" }} />
              </div>
              <div className="dash-progress-label mono dim">
                gen {curGen} / {totalGens}{etaSec != null ? ` · eta ${fmtEta(etaSec)}` : ""}
              </div>
            </div>
          )}

          <div className="windows">
            <Window title="Fitness" area="fitness"><FitnessCanvas rows={genRows} estMax={estMax} /></Window>
            <Window title="Behavior" area="behavior"><BehaviorCanvas behavior={behavior} /></Window>
            <Window title={selEntry ? `Network — rank #${selEntry.rank + 1} (${selEntry.fitness.toFixed(3)})` : "Network — best"} area="network">
              <NetworkSvg genome={shownGenome} topology={topology} descriptor={archDescriptor} motors={selEntry ? null : motorOut} />
            </Window>
            <Window title="Flight" area="flight" tall>
              <Suspense fallback={<div className="viz-empty">loading 3D…</div>}>
                <FlightPath3D behavior={behavior} multi={behaviorMulti} />
              </Suspense>
            </Window>
            <Window title="Phylogeny" area="phylo" tall><PhyloSvg key={name} lineage={lineage} /></Window>
            <Window title="Fitness landscape" area="landscape" tall>
              <Suspense fallback={<div className="viz-empty">loading 3D…</div>}>
                <FitnessLandscape3D key={name} lineage={lineage} />
              </Suspense>
            </Window>
          </div>
        </>
      )}
    </div>
  );
}

// Bolt-on trigger: a dismissible banner that appears on the evolution dashboard
// only while a gradient run is actively writing telemetry, linking to /gradient.
// (No permanent tab — the gradient view surfaces only when it's relevant.)
// Dismissal is per-session; a newly-started run (live rising edge) re-shows it.
function GradientBanner() {
  const live = useGradientStatus().data?.live ?? false;
  const [dismissed, setDismissed] = useState(false);
  const prevLive = useRef(false);
  useEffect(() => {
    if (live && !prevLive.current) setDismissed(false);
    prevLive.current = live;
  }, [live]);
  if (!live || dismissed) return null;
  return (
    <div className="grad-banner">
      <Link to="/gradient" className="grad-banner-link">⚡ Gradient training live — open Gradient console →</Link>
      <button className="grad-banner-x" onClick={() => setDismissed(true)} aria-label="Dismiss">✕</button>
    </div>
  );
}

function ConnDot({ conn }: { conn: string }) {
  const color = conn === "live" ? "var(--good)" : conn === "stale" ? "var(--warn)" : "var(--fg-dim)";
  return <><span style={{ color }}>●</span> {conn}</>;
}
